import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { ArrowRight, ChevronDown, Menu, X, Phone, Mail, MapPin, Download } from 'lucide-react';
import { SITE, NAV, EQUIPMENT, SERVICES, INDUSTRIES, IMG } from '../data/site';
import { Reveal } from './motion';

/* ---------------- Logo (unchanged brand asset) ---------------- */
export function Logo({ dark = true }) {
  return (
    <Link to="/" className="flex items-center group shrink-0" data-testid="nav-logo-link" aria-label="ToxGlobal Analytics home">
      <img
        src="/logo.png"
        alt="ToxGlobal Analytics logo"
        width={1536}
        height={1024}
        className={`h-12 lg:h-14 xl:h-16 w-auto transition-opacity duration-200 group-hover:opacity-85 ${dark ? '' : 'opacity-90'}`}
      />
    </Link>
  );
}

/* ---------------- Mega menu panel ---------------- */
function MenuPanel({ kind }) {
  const data = kind === 'equipment' ? EQUIPMENT : kind === 'services' ? SERVICES : INDUSTRIES;
  const hrefBase = kind === 'equipment' ? '/equipment/' : kind === 'services' ? '/services/' : '/industries/';
  const hub = kind === 'equipment' ? '/equipment' : kind === 'services' ? '/services' : '/industries';
  const hubLabel = kind === 'equipment' ? 'All equipment categories' : kind === 'services' ? 'All services' : 'All industries';
  return (
    <div className="invisible opacity-0 translate-y-2 group-hover:visible group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-200 absolute left-0 top-full pt-3 z-50">
      <div className="on-light bg-white border border-slate-200 shadow-xl shadow-black/20 w-[560px] max-w-[92vw]">
        <div className="max-h-[70vh] overflow-y-auto p-2">
          {[...data.map((d) => ({ label: d.name, href: hrefBase + d.slug })), { label: hubLabel, href: hub }].map((l) => (
            <Link
              key={l.href + l.label}
              to={l.href}
              data-testid={`nav-${kind}-link-${l.href.split('/').pop() || 'hub'}`}
              className="flex items-center justify-between px-4 py-2.5 text-sm text-slate-700 hover:bg-stone-50 hover:text-bronze transition-colors group/item"
            >
              <span>{l.label}</span>
              <ArrowRight size={14} className="opacity-0 group-hover/item:opacity-100 -translate-x-1 group-hover/item:translate-x-0 transition-all" />
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ---------------- Navigation ---------------- */
export function Nav() {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const location = useLocation();
  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header className="sticky top-0 z-50" data-testid="site-header">
      <div className="hidden md:block bg-ink text-white border-b border-white/10">
        <div className="container-x flex items-center justify-between py-2">
          <div className="flex items-center gap-6 font-mono text-[11px] tracking-[0.14em] uppercase text-stone-400">
            <span>SAM.gov: <span className="text-gold">Active</span></span>
            <span>UEI {SITE.uei}</span>
            <span>CAGE {SITE.cage}</span>
            <span className="hidden lg:inline">Nationwide · USA</span>
          </div>
          <div className="flex items-center gap-5 font-mono text-[11px] tracking-[0.14em] uppercase">
            <a href={SITE.phoneHref} className="flex items-center gap-1.5 hover:text-gold transition-colors" data-testid="nav-phone-link"><Phone size={12} /> {SITE.phone}</a>
            <a href={`mailto:${SITE.email}`} className="flex items-center gap-1.5 hover:text-gold transition-colors" data-testid="nav-email-link"><Mail size={12} /> {SITE.email}</a>
          </div>
        </div>
      </div>

      <div className="bg-ink/95 backdrop-blur-xl border-b border-white/10">
        <div className="container-x flex items-center justify-between h-16 sm:h-20">
          <Logo />
          <nav className="hidden lg:flex items-center gap-7" aria-label="Primary">
            {NAV.map((item) =>
              item.menu ? (
                <div key={item.label} className="relative group">
                  <Link
                    to={item.href}
                    data-testid={`nav-${item.label.toLowerCase()}-link`}
                    className="flex items-center gap-1 text-sm font-medium text-stone-200 hover:text-gold transition-colors py-2"
                  >
                    {item.label} <ChevronDown size={14} className="opacity-60" />
                  </Link>
                  <MenuPanel kind={item.menu} />
                </div>
              ) : (
                <NavLink
                  key={item.label}
                  to={item.href}
                  data-testid={`nav-${item.label.toLowerCase()}-link`}
                  className={({ isActive }) =>
                    `link-sweep text-sm font-medium py-2 transition-colors ${isActive ? 'text-gold' : 'text-stone-200 hover:text-gold'}`
                  }
                >
                  {item.label}
                </NavLink>
              )
            )}
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/contact" className="btn-gold hidden sm:inline-flex !px-5 !py-2.5" data-testid="nav-cta-request-quote">
              Request a Quote <ArrowRight size={14} />
            </Link>
            <button
              className="lg:hidden p-2 text-white"
              onClick={() => setOpen(!open)}
              aria-label={open ? 'Close menu' : 'Open menu'}
              data-testid="nav-mobile-toggle"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-ink border-b border-white/10 shadow-2xl max-h-[80vh] overflow-y-auto" data-testid="nav-mobile-menu">
          <div className="container-x py-4">
            {NAV.map((item) =>
              item.menu ? (
                <div key={item.label} className="border-b border-white/10">
                  <div className="flex items-center justify-between">
                    <Link to={item.href} className="py-3 font-display font-semibold text-white" data-testid={`mobile-${item.label.toLowerCase()}-link`}>
                      {item.label}
                    </Link>
                    <button
                      className="p-2 text-white"
                      aria-label={`Toggle ${item.label} submenu`}
                      data-testid={`mobile-${item.label.toLowerCase()}-toggle`}
                      onClick={() => setExpanded(expanded === item.menu ? null : item.menu)}
                    >
                      <ChevronDown size={18} className={`transition-transform ${expanded === item.menu ? 'rotate-180' : ''}`} />
                    </button>
                  </div>
                  {expanded === item.menu && (
                    <div className="pb-3">
                      {(item.menu === 'equipment' ? EQUIPMENT : item.menu === 'services' ? SERVICES : INDUSTRIES).map((d) => (
                        <Link key={d.slug} to={item.href + '/' + d.slug} className="block py-1.5 pl-3 text-sm text-stone-400 hover:text-gold transition-colors" data-testid={`mobile-${item.menu}-${d.slug}`}>
                          {d.name}
                        </Link>
                      ))}
                      <Link to={item.href} className="block py-1.5 pl-3 mt-1 text-sm font-semibold text-gold" data-testid={`mobile-${item.menu}-hub`}>
                        View all {item.label.toLowerCase()} →
                      </Link>
                    </div>
                  )}
                </div>
              ) : (
                <Link key={item.label} to={item.href} className="block py-3 border-b border-white/10 font-display font-semibold text-white" data-testid={`mobile-${item.label.toLowerCase()}-link`}>
                  {item.label}
                </Link>
              )
            )}
            <Link to="/contact" className="btn-gold w-full mt-5" data-testid="mobile-cta-request-quote">Request a Quote</Link>
          </div>
        </div>
      )}
    </header>
  );
}

/* ---------------- Breadcrumbs ---------------- */
export function Breadcrumbs({ items }) {
  return (
    <nav aria-label="Breadcrumb" className="mb-6" data-testid="breadcrumbs">
      <ol className="flex flex-wrap items-center gap-2 font-mono text-[11px] uppercase tracking-[0.15em]">
        {items.map((it, i) => (
          <li key={it.href || it.label} className="flex items-center gap-2">
            {i > 0 && <span className="text-slate-400">/</span>}
            {it.href ? (
              <Link to={it.href} className="text-slate-500 hover:text-bronze transition-colors">{it.label}</Link>
            ) : (
              <span className="text-bronze" aria-current="page">{it.label}</span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

/* ---------------- Interior page hero ---------------- */
export function PageHero({ eyebrow, title, lines, sub, breadcrumbs, image, imageAlt, dark = false }) {
  return (
    <section className={`${dark ? 'spotlight-dark grain text-white' : 'bg-paper text-ink'} border-b border-[#E7E2D9] ${dark ? 'on-dark' : ''}`}>
      <div className="container-x py-14 sm:py-20">
        {breadcrumbs && <Breadcrumbs items={breadcrumbs} />}
        <div className="grid lg:grid-cols-[1.2fr_0.8fr] gap-10 items-end">
          <div>
            {eyebrow && <Reveal><p className={`eyebrow mb-4 text-bronze ${dark ? 'on-dark !text-gold' : ''}`}>{eyebrow}</p></Reveal>}
            <Reveal delay={0.05}>
              <h1 className="display text-4xl sm:text-5xl lg:text-6xl">{lines || title}</h1>
            </Reveal>
            {sub && <Reveal delay={0.12}><p className={`mt-5 max-w-2xl leading-relaxed ${dark ? 'text-stone-300' : 'text-stone-600'}`}>{sub}</p></Reveal>}
          </div>
          {image && (
            <Reveal delay={0.15} className="hidden lg:block">
              <div className="clipped-frame overflow-hidden bg-midnight aspect-[16/10]">
                <img src={image} alt={imageAlt || ''} loading="lazy" className="h-full w-full object-cover opacity-90" />
              </div>
            </Reveal>
          )}
        </div>
      </div>
    </section>
  );
}

/* ---------------- CTA band ---------------- */
export function CTABand({ title = 'Ready to source, install, or support an instrument?', sub = 'Send your requirement — most RFQs receive a formatted quote with procurement documentation.' }) {
  return (
    <section className="spotlight-dark grain text-white on-dark" data-testid="cta-band">
      <div className="container-x py-16 sm:py-20">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <Reveal>
            <p className="eyebrow text-gold mb-3">Next step</p>
            <h2 className="display text-3xl sm:text-4xl lg:text-5xl max-w-2xl">{title}</h2>
            <p className="mt-4 text-stone-300 max-w-xl">{sub}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="flex flex-wrap gap-3">
              <Link to="/contact" className="btn-gold" data-testid="cta-band-request-quote">Request a Quote <ArrowRight size={15} /></Link>
              <Link to="/government" className="btn-outline-light" data-testid="cta-band-send-rfq">Send an RFQ</Link>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ---------------- Footer ---------------- */
function FootCol({ title, links }) {
  return (
    <div>
      <h3 className="eyebrow text-gold mb-4">{title}</h3>
      <ul className="space-y-2">
        {links.map((l) => (
          <li key={l.href + l.label}>
            <Link to={l.href} className="text-sm text-stone-400 hover:text-gold transition-colors" data-testid={`footer-link-${(l.href + l.label).toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>{l.label}</Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function Footer() {
  return (
    <footer className="bg-ink text-white" data-testid="site-footer">
      <div className="container-x py-14 sm:py-16">
        <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-5">
          <div className="lg:col-span-2">
            <Logo dark />
            <p className="mt-5 text-sm text-stone-400 max-w-sm leading-relaxed">
              ToxGlobal Analytics supplies, installs, and supports analytical instrumentation for government and regulated
              laboratories — from equipment sourcing through installation, validation, service, and lifecycle support.
            </p>
            <div className="mt-6 space-y-2 font-mono text-[11px] uppercase tracking-[0.14em] text-stone-300">
              <p>UEI {SITE.uei} · CAGE {SITE.cage}</p>
              <p>SAM.gov: <span className="text-gold">Active</span> · Small Business ({SITE.smallBusiness})</p>
              <p>NAICS 334516 · PSC 6640</p>
            </div>
            <div className="mt-6 space-y-2 text-sm">
              <a href={SITE.phoneHref} className="flex items-center gap-2 text-stone-300 hover:text-gold transition-colors" data-testid="footer-phone-link"><Phone size={14} /> {SITE.phone}</a>
              <a href={`mailto:${SITE.email}`} className="flex items-center gap-2 text-stone-300 hover:text-gold transition-colors" data-testid="footer-email-link"><Mail size={14} /> {SITE.email}</a>
              <p className="flex items-center gap-2 text-stone-400"><MapPin size={14} /> Serving all 50 states nationwide</p>
            </div>
            <a
              href="/capability-statement.pdf" download
              className="mt-6 inline-flex items-center gap-2 border border-white/25 px-4 py-2.5 text-sm font-medium hover:border-gold hover:text-gold transition-colors"
              data-testid="footer-capability-download"
            >
              <Download size={14} /> Capability Statement (PDF)
            </a>
          </div>
          <FootCol title="Equipment" links={EQUIPMENT.map((e) => ({ label: e.name, href: `/equipment/${e.slug}` }))} />
          <FootCol title="Services" links={SERVICES.map((s) => ({ label: s.name, href: `/services/${s.slug}` }))} />
          <div>
            <FootCol title="Company" links={[
              { label: 'Government Contracting', href: '/government' },
              { label: 'Industries', href: '/industries' },
              { label: 'Manufacturers & Partners', href: '/manufacturers-partners' },
              { label: 'About', href: '/about' },
              { label: 'Insights', href: '/insights' },
              { label: 'Contact / RFQ', href: '/contact' },
            ]} />
          </div>
        </div>
        <div className="mt-12 pt-6 border-t border-white/10 flex flex-col md:flex-row justify-between gap-3 text-xs text-stone-500">
          <p>© 2026 ToxGlobal Analytics. All rights reserved.</p>
          <p className="font-mono uppercase tracking-[0.12em]">Analytical Equipment. Installed. Supported.</p>
        </div>
      </div>
    </footer>
  );
}

/* ---------------- Shared link row (services index style) ---------------- */
export function LinkRow({ i, href, label, tag }) {
  return (
    <Reveal delay={Math.min(i * 0.03, 0.3)}>
      <Link
        to={href}
        className="group flex items-center justify-between gap-4 py-4 px-2 sm:px-4 hairline-b hover:bg-stone-50 transition-colors"
        data-testid={`link-row-${href.split('/').filter(Boolean).join('-')}`}
      >
        <div className="flex items-baseline gap-4 sm:gap-6">
          <span className="font-mono text-xs text-stone-400 tabular w-7">{String(i + 1).padStart(2, '0')}</span>
          <div>
            <span className="font-display font-semibold text-lg text-ink group-hover:text-bronze transition-colors">{label}</span>
            {tag && <span className="block text-sm text-stone-500">{tag}</span>}
          </div>
        </div>
        <ArrowRight size={18} className="text-stone-400 group-hover:text-bronze group-hover:translate-x-1 transition-all shrink-0" />
      </Link>
    </Reveal>
  );
}

export const HOME_IMG = IMG;
